export const HELP_GUIDE_PDF_FILENAME = "queue-management-reports-help-guide.pdf";
const PDF_IMAGE_WIDTH = 500;

let fontsRegistered = false;
let pdfMakeInstance = null;

const ensurePdfMakeFonts = async () => {
  if (!pdfMakeInstance) {
    const [pdfMakeModule, pdfFontsModule] = await Promise.all([
      import("pdfmake/build/pdfmake"),
      import("pdfmake/build/vfs_fonts"),
    ]);

    pdfMakeInstance = pdfMakeModule.default || pdfMakeModule;

    if (!fontsRegistered) {
      pdfMakeInstance.addVirtualFileSystem(pdfFontsModule.default || pdfFontsModule);
      fontsRegistered = true;
    }
  }

  return pdfMakeInstance;
};

const asArray = (value) => (Array.isArray(value) ? value : []);

export const formatHelpGuidePdfDate = (date = new Date()) =>
  new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(date instanceof Date ? date : new Date(date));

export const getHelpGuideScreenshots = (guides = []) => {
  const screenshots = [];
  const seen = new Set();

  asArray(guides).forEach((guide) => {
    [...asArray(guide.screenshots), ...asArray(guide.steps).map((step) => step.screenshot)]
      .filter(Boolean)
      .forEach((screenshot) => {
        if (!screenshot.src || seen.has(screenshot.src)) return;

        seen.add(screenshot.src);
        screenshots.push(screenshot);
      });
  });

  return screenshots;
};

const resolveAssetUrl = (src) => {
  if (/^https?:\/\//i.test(src)) return src;
  return `${window.location.origin}${src.startsWith("/") ? src : `/${src}`}`;
};

const loadBlobImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = url;
  });

export const convertScreenshotToPdfImage = async (screenshot, cache = new Map()) => {
  if (!screenshot?.src) return null;
  if (cache.has(screenshot.src)) return cache.get(screenshot.src);

  const response = await fetch(resolveAssetUrl(screenshot.src));
  if (!response.ok) throw new Error(`Unable to load screenshot ${screenshot.src}`);

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);

  try {
    const image = await loadBlobImage(objectUrl);
    const canvas = document.createElement("canvas");
    const width = image.naturalWidth || image.width;
    const height = image.naturalHeight || image.height;

    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext("2d");
    if (!context) throw new Error(`Unable to prepare screenshot ${screenshot.src}`);

    context.drawImage(image, 0, 0, width, height);

    const dataUrl = canvas.toDataURL("image/jpeg", 0.86);
    cache.set(screenshot.src, dataUrl);

    return dataUrl;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

export const resolveHelpGuidePdfImages = async (guides = [], imageResolver = convertScreenshotToPdfImage) => {
  const cache = new Map();
  const entries = await Promise.all(
    getHelpGuideScreenshots(guides).map(async (screenshot) => {
      try {
        return [screenshot.src, await imageResolver(screenshot, cache)];
      } catch (error) {
        return [screenshot.src, null];
      }
    })
  );

  return Object.fromEntries(entries);
};

const screenshotBlock = (screenshot, imageDataBySrc) => {
  const image = imageDataBySrc[screenshot.src];
  const caption = screenshot.alt || "Queue Management Reports screenshot";

  if (!image) {
    return {
      text: `Screenshot unavailable: ${caption}`,
      style: "missingScreenshot",
      margin: [0, 8, 0, 10],
    };
  }

  return [
    {
      image,
      width: PDF_IMAGE_WIDTH,
      alignment: "left",
      margin: [0, 10, 0, 4],
    },
    {
      text: caption,
      style: "imageCaption",
      margin: [0, 0, 0, 12],
    },
  ];
};

const screenshotBlocks = (screenshot, imageDataBySrc) => {
  const block = screenshotBlock(screenshot, imageDataBySrc);
  return Array.isArray(block) ? block : [block];
};

const metadataRow = (label, value) => ({
  columns: [
    { text: label, style: "metadataLabel", width: 88 },
    { text: value || "Not set", style: "metadataValue", width: "*" },
  ],
  columnGap: 8,
  margin: [0, 0, 0, 4],
});

const guideScreenshotBlocks = (guide, imageDataBySrc) => {
  const stepScreenshotSources = new Set(
    asArray(guide.steps)
      .map((step) => step.screenshot?.src)
      .filter(Boolean)
  );

  return asArray(guide.screenshots)
    .filter((screenshot) => !stepScreenshotSources.has(screenshot.src))
    .flatMap((screenshot) => screenshotBlocks(screenshot, imageDataBySrc));
};

const buildGuideSection = (guide, guideIndex, guidesById, imageDataBySrc) => {
  const guideNumber = guideIndex + 1;
  const tags = asArray(guide.tags).join(", ");
  const relatedGuides = asArray(guide.relatedGuides)
    .map((relatedGuideId) => guidesById.get(relatedGuideId)?.title)
    .filter(Boolean);

  return [
    {
      text: `Guide ${guideNumber}: ${guide.title}`,
      style: "guideTitle",
      tocItem: true,
      headlineLevel: 1,
      id: guide.id,
      pageBreak: guideIndex === 0 ? undefined : "before",
    },
    {
      stack: [
        metadataRow("Category", guide.category),
        metadataRow("Updated", guide.updatedAt),
        metadataRow("Tags", tags),
      ],
      margin: [0, 0, 0, 12],
    },
    { text: guide.summary, style: "guideSummary" },
    ...guideScreenshotBlocks(guide, imageDataBySrc),
    ...asArray(guide.steps).flatMap((step, stepIndex) => [
      {
        text: `${guideNumber}.${stepIndex + 1} ${step.title}`,
        style: "stepTitle",
        margin: [0, stepIndex === 0 ? 6 : 12, 0, 3],
      },
      { text: step.body, style: "bodyText" },
      ...(step.screenshot ? screenshotBlocks(step.screenshot, imageDataBySrc) : []),
    ]),
    ...(relatedGuides.length
      ? [
          {
            text: "Related guides",
            style: "relatedTitle",
            margin: [0, 14, 0, 4],
          },
          {
            ul: relatedGuides,
            style: "bodyText",
          },
        ]
      : []),
  ];
};

export const buildHelpGuidePdfDefinition = (
  guides = [],
  { generatedAt = new Date(), imageDataBySrc = {} } = {}
) => {
  const generatedDate = formatHelpGuidePdfDate(generatedAt);
  const screenshotCount = getHelpGuideScreenshots(guides).length;
  const guidesById = new Map(guides.map((guide) => [guide.id, guide]));

  return {
    pageSize: "A4",
    pageMargins: [44, 52, 44, 54],
    info: {
      title: "Queue Management Reports Help Guide",
      subject: "Complete user help manual",
      creator: "Queue Management Reports",
      producer: "Queue Management Reports",
    },
    footer: (currentPage, pageCount) => {
      if (currentPage === 1) return { text: "" };

      return {
        columns: [
          { text: "Queue Management Reports Help Guide", alignment: "left" },
          { text: `Page ${currentPage} of ${pageCount}`, alignment: "right" },
        ],
        margin: [44, 0, 44, 0],
        fontSize: 8,
        color: "#6b7280",
      };
    },
    content: [
      {
        stack: [
          { text: "Queue Management Reports Help Guide", style: "coverTitle" },
          { text: "Complete user help manual", style: "coverSubtitle" },
          {
            text: `Generated ${generatedDate}`,
            style: "coverMeta",
            margin: [0, 28, 0, 0],
          },
          {
            text: `${guides.length} guides • ${screenshotCount} screenshots`,
            style: "coverMeta",
          },
        ],
        margin: [0, 150, 0, 0],
        pageBreak: "after",
      },
      {
        toc: {
          title: { text: "Table of contents", style: "tocTitle" },
          numberStyle: { color: "#6b7280" },
        },
        pageBreak: "after",
      },
      ...guides.flatMap((guide, guideIndex) =>
        buildGuideSection(guide, guideIndex, guidesById, imageDataBySrc)
      ),
    ],
    defaultStyle: {
      font: "Roboto",
      fontSize: 10,
      color: "#111827",
      lineHeight: 1.25,
    },
    styles: {
      coverTitle: { fontSize: 30, bold: true, color: "#111827", lineHeight: 1.1 },
      coverSubtitle: { fontSize: 15, color: "#4b5563", margin: [0, 10, 0, 0] },
      coverMeta: { fontSize: 11, color: "#6b7280" },
      tocTitle: { fontSize: 22, bold: true, color: "#111827", margin: [0, 0, 0, 16] },
      guideTitle: { fontSize: 20, bold: true, color: "#111827", margin: [0, 0, 0, 12] },
      guideSummary: { fontSize: 11, color: "#374151", lineHeight: 1.35, margin: [0, 0, 0, 12] },
      metadataLabel: { fontSize: 8, bold: true, color: "#6b7280" },
      metadataValue: { fontSize: 8, color: "#374151" },
      stepTitle: { fontSize: 13, bold: true, color: "#111827" },
      bodyText: { fontSize: 10, color: "#374151", lineHeight: 1.35 },
      imageCaption: { fontSize: 8, italics: true, color: "#6b7280" },
      missingScreenshot: { fontSize: 9, italics: true, color: "#9f1239" },
      relatedTitle: { fontSize: 11, bold: true, color: "#111827" },
    },
  };
};

export const downloadHelpGuidePdf = async (
  guides = [],
  { filename = HELP_GUIDE_PDF_FILENAME, generatedAt = new Date(), imageResolver } = {}
) => {
  const imageDataBySrc = await resolveHelpGuidePdfImages(guides, imageResolver);
  const definition = buildHelpGuidePdfDefinition(guides, { generatedAt, imageDataBySrc });

  const pdfMake = await ensurePdfMakeFonts();

  pdfMake.createPdf(definition).download(filename);
};
